import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useFinalizeIncidentReport,
  useIncidentReport,
  useUpdateIncidentReport,
  type IncidentReportV1,
} from "@/api/queries/incidents";
import { useAuth } from "@/auth/AuthProvider";
import { useCurrentUser } from "@/api/queries/me";
import { canEditReport } from "@/lib/permissions";
import { printReport } from "@/lib/print-report";
import { useDownloadReportPdf } from "@/routes/incidents/useDownloadReportPdf";
import type { components } from "@/api/types";

function arrayField(value: string): string[] {
  return value
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

// The statuses a chief can sign off. Mirrors the API's own guard on
// POST /report/finalize: `rejected` holds a Bedrock error rather than a
// report, and `finalized` is already signed. Typed with the generated status
// union so the compiler refuses a value the API never returns. (RES-387: this
// once waited for a status the database cannot hold, so Finalize never
// enabled.)
const FINALIZABLE: ReadonlySet<components["schemas"]["ReportRead"]["status"]> =
  new Set(["draft", "reviewed"]);

type Props = { incidentId: string };

export function ReportEditor({ incidentId }: Props) {
  const { identity } = useAuth();
  const me = useCurrentUser();
  const editable = canEditReport(identity?.role);
  const { data, isLoading } = useIncidentReport(incidentId);
  const update = useUpdateIncidentReport(incidentId);
  const finalize = useFinalizeIncidentReport(incidentId);
  const pdf = useDownloadReportPdf(incidentId);

  const [hazards, setHazards] = useState("");
  const [decon, setDecon] = useState("");
  const [gaps, setGaps] = useState("");
  const [notes, setNotes] = useState("");
  // The PATCH typically returns in well under a second, so the transient
  // "Saving…" label was invisible and a successful save looked identical to a
  // dead button (three identical 200s from one confused chief, 2026-07-30).
  // A save has to SAY it saved.
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!data) return;
    setHazards(data.body.hazards_identified.join("\n"));
    setDecon(data.body.decon_actions.join("\n"));
    setGaps(data.body.gaps.join("\n"));
    setNotes(data.body.after_action_notes);
  }, [data]);

  if (isLoading) return <Skeleton className="h-64" />;
  if (!data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          No report draft yet. It's generated when the incident closes.
        </CardContent>
      </Card>
    );
  }

  const locked = data.status === "finalized" || !editable;

  const onSave = () => {
    const next: IncidentReportV1 = {
      ...data.body,
      hazards_identified: arrayField(hazards),
      decon_actions: arrayField(decon),
      gaps: arrayField(gaps),
      after_action_notes: notes,
    };
    update.mutate(next, { onSuccess: () => setSavedAt(new Date()) });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Report draft: {data.status}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <FieldList
            label="Hazards identified"
            value={hazards}
            onChange={setHazards}
            disabled={locked}
          />
          <FieldList
            label="Decon actions"
            value={decon}
            onChange={setDecon}
            disabled={locked}
          />
          <FieldList
            label="Gaps"
            value={gaps}
            onChange={setGaps}
            disabled={locked}
          />
          <div className="space-y-1">
            <label className="text-sm font-medium">After-action notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={locked}
              rows={6}
            />
          </div>
        </div>

        {data.markdown ? (
          <details className="rounded-md border border-border p-3 text-sm">
            <summary className="cursor-pointer text-muted-foreground">
              Preview markdown
            </summary>
            <pre className="mt-2 whitespace-pre-wrap text-xs">
              {data.markdown}
            </pre>
          </details>
        ) : null}

        <div className="flex items-center gap-2">
          <Button
            data-testid="save-report"
            onClick={onSave}
            disabled={locked || update.isPending}
          >
            {update.isPending ? "Saving…" : "Save draft"}
          </Button>
          <Button
            variant="destructive"
            data-testid="finalize-report"
            disabled={locked || finalize.isPending || !FINALIZABLE.has(data.status)}
            onClick={() => finalize.mutate()}
          >
            {finalize.isPending ? "Finalizing…" : "Finalize"}
          </Button>
          {data.pdf_s3_key ? (
            <Button
              variant="outline"
              data-testid="download-report-pdf"
              disabled={pdf.isPending}
              // Presign + attachment-navigation live in the shared hook — one
              // mechanism for this row and the report header, per RES-226.
              onClick={pdf.download}
            >
              {pdf.isPending ? "Preparing…" : "Download PDF"}
            </Button>
          ) : (
            // No worker-rendered PDF — which is EVERY sandbox incident, by
            // design (the source PDF carries the source department's name, so
            // the copy drops it). Fall back to printing the report that is on
            // screen, branded for this tenant. The button lives here, beside
            // Save draft and Finalize, because that is the row a chief looks
            // at after saving; the copy at the top of the report is the same
            // action for someone reading rather than editing.
            <Button
              variant="outline"
              data-testid="print-report-pdf-editor"
              // Raw, unguarded: printReport owns the fallback, so this cannot
              // disagree with the letterhead the same print produces. The
              // title differs from the header button's ("Incident Narrative"
              // vs the typed report's own title) — that names the section a
              // chief printed from; the printed CONTENT is the same DOM.
              onClick={() =>
                printReport(me.data?.department?.name, "Incident Narrative")
              }
            >
              Download PDF
            </Button>
          )}
          {data.status === "finalized" ? (
            <span className="text-xs text-muted-foreground">
              Locked: finalized.
            </span>
          ) : null}
          {savedAt && !update.isPending && !update.isError ? (
            <span data-testid="report-saved-at" className="text-xs text-muted-foreground">
              Saved ✓{" "}
              {savedAt.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </span>
          ) : null}
        </div>
        {update.isError ? (
          <p className="text-xs text-destructive">
            {update.error instanceof Error
              ? update.error.message
              : "Could not save the draft."}
          </p>
        ) : null}
        {finalize.isError ? (
          // A refused finalize (409: another chief signed it first) or any
          // other failure must say so; the hook refetches the draft on error,
          // so a lost race also flips the view to finalized and locks it.
          <p data-testid="finalize-error" className="text-xs text-destructive">
            {finalize.error instanceof Error
              ? finalize.error.message
              : "Could not finalize the report."}
          </p>
        ) : null}
        {pdf.isError ? (
          <p className="text-xs text-destructive">
            Could not download the report PDF.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function FieldList({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={5}
        placeholder="One item per line"
      />
    </div>
  );
}
